import React from "react";
import RecognitionGuideLayout from "@/components/RecognitionGuideLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Users, Settings, Smartphone, BarChart3, Award, Zap, CheckCircle, BookOpen, Calculator, TrendingUp, ArrowRight, ExternalLink, Plus, Minus } from "lucide-react";

export { Page }

function Page() {
  const coreTools = [
    {
      title: "Digital Recognition Platform",
      description: "A centralized system where employees can give and receive recognition. It should support peer-to-peer recognition, milestones, values alignment, and rewards.",
      icon: Smartphone,
      link: { text: "What Is Employee Recognition Software?", url: "/blog/best-employee-recognition-software-platforms-top-10-for-2025" }
    },
    {
      title: "Integrations with Collaboration Tools",
      description: "Recognition should happen where people work-Slack, Microsoft Teams, email, or mobile. Tools that integrate seamlessly make recognition a daily habit instead of a side task.",
      icon: Settings,
      link: { text: "Slack and Teams integrations", url: "/integrations" }
    },
    {
      title: "Automated Triggers for Milestones",
      description: "Work anniversaries, birthdays, and promotions should never be missed. Automations ensure consistency while freeing HR from manual reminders.",
      icon: Zap,
      link: { text: "Best Practices for Celebrating Milestones and Anniversaries", url: "/blog/best-practices-for-celebrating-milestones-and-anniversaries" }
    },
    {
      title: "Rewards Catalog",
      description: "Recognition paired with meaningful rewards creates lasting impact. Look for flexibility-gift cards, swag, wellness stipends, experiences-so every employee has options that resonate.",
      icon: Award,
      link: { text: "50 Best Personalized Reward Ideas", url: "/blog/50-best-personalized-reward-ideas-for-recognizing-employees" }
    },
    {
      title: "Analytics and Reporting",
      description: "To prove ROI, you need data. Track recognition frequency, equity across departments, and links to engagement and retention.",
      icon: BarChart3,
      link: { text: "How to Leverage Recognition Analytics for Enhanced Engagement", url: "/blog/how-to-leverage-employee-recognition-analytics-for-enhanced-engagement" }
    }
  ];

  const resourceCategories = [
    {
      title: "Guides & Playbooks",
      icon: BookOpen,
      resources: [
        { title: "Your Employee Recognition Program Ideas Guide", url: "/blog/your-employee-recognition-program-ideas-guide" },
        { title: "Your Complete Guide to Getting Started with Peer Recognition", url: "/blog/your-complete-guide-to-getting-started-with-peer-recognition" },
        { title: "How to Effectively Recognize Good Employee Performance", url: "/blog/how-to-effectively-recognize-good-employee-performance" }
      ]
    },
    {
      title: "Templates & Examples",
      icon: Calculator,
      resources: [
        { title: "Top 20 Employee Recognition Words and Messages", url: "/faq/top-20-employee-recognition-words-and-messages" },
        { title: "Crafting Meaningful Recognition Messages", url: "/blog/craft-meaningful-employee-recognition-with-the-right-words" },
        { title: "Appreciation Message Examples to Say Thank You", url: "/blog/employee-appreciation-message-examples-to-say-thank-you" }
      ]
    },
    {
      title: "Budget & Planning",
      icon: TrendingUp,
      resources: [
        { title: "Recognition Budget Planning: The Complete Guide", url: "/blog/employee-recognition-budget-planning-the-complete-guide" },
        { title: "Crafting an Effective Budget-Friendly Recognition Program", url: "/blog/crafting-an-effective-budget-friendly-recognition-program" }
      ]
    },
    {
      title: "Research & Trends",
      icon: BarChart3,
      resources: [
        { title: "Top Employee Recognition Trends Changing Today's Workplace", url: "/blog/top-employee-recognition-trends-changing-todays-workplace" },
        { title: "The Rising Importance of Employee Recognition in Workplaces", url: "/blog/the-rising-importance-of-employee-recognition-in-workplaces" },
        { title: "Employee Recognition Engagement Trends", url: "/blog/employee-recognition-engagement-trends" }
      ]
    }
  ];

  return (
    <RecognitionGuideLayout
      title="Employee Recognition Tools and Resources"
      description="Discover essential employee recognition tools, platforms, templates, and resources. Get checklists, implementation guides, and find the right recognition technology for your organization."
      currentStep={6}
    >
      <div className="space-y-12">
        {/* Header */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium border border-primary/20">
            <Users className="w-4 h-4" />
            Step 6 of 6
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            Employee Recognition Tools and Resources
          </h1>
          
          <div className="space-y-4">
            <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
              Recognition works best when it's consistent, visible, and easy to give. That's where the right tools and resources come in. Without them, even the best-intentioned programs fizzle out-because employees and managers won't keep up if recognition feels like extra work.
            </p>
            
            <p className="text-muted-foreground max-w-4xl leading-relaxed">
              The good news: modern platforms like Assembly make it simple to embed recognition into the flow of work while providing the guardrails and data HR leaders need. For a comprehensive comparison of available solutions, our <a href="https://joinassembly.com/best/employee-recognition-software/top" className="text-primary hover:text-primary/80 underline">Best Employee Recognition Software</a> guide provides detailed analysis and vendor comparisons to help you choose the right platform.
            </p>
          </div>
        </div>

        {/* Core Tools Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Core Tools Every Recognition Program Needs
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              These five essential tools form the foundation of any successful recognition program
            </p>
          </div>

          <div className="grid gap-6 md:gap-8">
            {coreTools.map((tool, index) => {
              const IconComponent = tool.icon;
              return (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-border/60 bg-card/50 backdrop-blur-sm">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex items-start">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                          <span className="text-lg font-semibold text-primary">{index + 1}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            {tool.title}
                          </h3>
                          <p className="text-muted-foreground leading-relaxed">
                            {tool.description}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm">
                          <ExternalLink className="w-4 h-4 text-primary" />
                          <a 
                            href={tool.link.url} 
                            className="text-primary hover:text-primary/80 transition-colors font-medium underline underline-offset-2"
                          >
                            {tool.link.text}
                          </a>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Resources Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Resources to Strengthen Your Program
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive guides, templates, and research to support your recognition initiative
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {resourceCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card key={index} className="border-border/60 bg-card/50 backdrop-blur-sm hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg">
                      <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center border border-secondary/20">
                        <IconComponent className="w-5 h-5 text-secondary" />
                      </div>
                      {category.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="space-y-3">
                      {category.resources.map((resource, resourceIndex) => (
                        <li key={resourceIndex} className="group">
                          <a 
                            href={resource.url} 
                            className="flex items-start gap-3 p-2 -m-2 rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors mt-0.5 shrink-0" />
                            <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors leading-relaxed">
                              {resource.title}
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Comprehensive Series CTA */}
        <section>
          <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
            <CardContent className="p-6 md:p-8 space-y-6 md:space-y-8">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                
                <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                  ✨ Ready to Transform Recognition at Your Company?
                </h2>
                
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                  Building a culture of recognition doesn't stop with ideas-it comes to life when you have the right tools in place. Assembly makes it simple to:
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                    <div className="text-muted-foreground">
                      Celebrate achievements with peer-to-peer and manager recognition.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                    <div className="text-muted-foreground">
                      Automate rewards for birthdays, milestones, and anniversaries.
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                    <div className="text-muted-foreground">
                      Integrate seamlessly with Slack, Microsoft Teams, and HRIS systems.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                    <div className="text-muted-foreground">
                      Track real impact with analytics and reporting dashboards.
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center space-y-6">
                <p className="text-lg font-medium text-foreground max-w-2xl mx-auto">
                  👉 Recognition isn't just a nice-to-have-it's the foundation of engagement, retention, and productivity.
                </p>
                
                <div className="space-y-4">
                  <p className="text-lg font-semibold text-foreground">Take the next step:</p>
                  
                  {/* Primary CTA */}
                  <div className="mb-4">
                    <Button size="lg" asChild className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground">
                      <a href="/solutions/recognition" className="flex items-center justify-center gap-2">
                        Explore Employee Recognition Features
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                  
                  {/* Secondary CTAs */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button size="lg" variant="outline" asChild className="w-full sm:w-auto">
                      <a href="/book-demo" className="flex items-center justify-center gap-2">
                        Book a Demo
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </Button>
                    <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                      <a href="/pricing" className="flex items-center justify-center gap-2">
                        Start Free Today
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* FAQ Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Common questions about recognition tools and platforms
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {[
              {
                question: "What features should I look for in employee recognition software?",
                answer: "Essential features include integration with collaboration tools (Slack, Teams), automated milestone tracking, mobile accessibility, analytics and reporting, flexible rewards catalog, and peer-to-peer recognition capabilities."
              },
              {
                question: "How much do employee recognition platforms cost?",
                answer: "Recognition platforms typically cost $3-15 per employee per month, depending on features and scale. Factor in additional costs for rewards, implementation, and training when budgeting for your program."
              },
              {
                question: "What integrations are most important for recognition tools?",
                answer: "The most valuable integrations are with collaboration tools (Slack, Microsoft Teams), HRIS systems for automated milestones, email platforms, and single sign-on (SSO) for seamless access across your tech stack."
              },
              {
                question: "How do I choose between different recognition platforms?",
                answer: "Evaluate platforms based on ease of use, integration capabilities, analytics depth, reward flexibility, mobile experience, customer support quality, and total cost of ownership including setup and ongoing management."
              }
            ].map((faq, index) => (
              <Collapsible key={index}>
                <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
                  <CollapsibleTrigger className="w-full group">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-left flex items-center justify-between">
                        <span className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {faq.question}
                        </span>
                        <div className="flex items-center">
                          <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=open]:hidden" />
                          <Minus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=closed]:hidden" />
                        </div>
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </section>

        {/* Closing Statement */}
        <div className="text-center space-y-4 py-8">
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            When recognition is powered by the right tools and supported with the right resources, it transforms from an HR initiative into a core driver of culture, engagement, and performance.
          </p>
        </div>
      </div>
    </RecognitionGuideLayout>
  );
}